import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { initSounds, playSoundIfEnabled } from '../utils/soundEffects';
import SoundToggle from './SoundToggle';

const { FiArrowLeft, FiPlus, FiTrash2, FiEdit, FiSettings, FiRefreshCw, FiSave, FiCheck, FiInfo, FiBox, FiGift } = FiIcons;

const MysteryBoxGame = () => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [newPlayer, setNewPlayer] = useState('');
  const [newChallenge, setNewChallenge] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [isShaking, setIsShaking] = useState(false);
  const [showPlayerInput, setShowPlayerInput] = useState(false);
  const [showChallengeInput, setShowChallengeInput] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editText, setEditText] = useState('');
  const [isEditingPlayer, setIsEditingPlayer] = useState(false);
  const [isEditingChallenge, setIsEditingChallenge] = useState(false);
  const [selectionType, setSelectionType] = useState('player'); // 'player' or 'challenge'
  const [showSettings, setShowSettings] = useState(false);
  const [boxColor, setBoxColor] = useState('pink'); // 'pink','blue','green','rainbow'
  const [challengeLevel, setChallengeLevel] = useState('mild'); // 'mild','spicy','hot'
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showPresetChallenges, setShowPresetChallenges] = useState(false);
  const [boxItems, setBoxItems] = useState([]);
  const [animatingItems, setAnimatingItems] = useState([]);
  const [showListPanel, setShowListPanel] = useState(false);

  const boxRef = useRef(null);

  // Initialize sounds when component mounts
  useEffect(() => {
    initSounds();
    
    // Load saved data from local storage
    const savedPlayers = localStorage.getItem('mysteryBoxPlayers');
    const savedChallenges = localStorage.getItem('mysteryBoxChallenges');
    const savedBoxColor = localStorage.getItem('mysteryBoxColor');
    const savedChallengeLevel = localStorage.getItem('mysteryBoxChallengeLevel');
    
    if (savedPlayers) setPlayers(JSON.parse(savedPlayers));
    if (savedChallenges) setChallenges(JSON.parse(savedChallenges));
    if (savedBoxColor) setBoxColor(savedBoxColor);
    if (savedChallengeLevel) setChallengeLevel(savedChallengeLevel);
    
    // Initialize with sample data if empty
    if (!savedPlayers || JSON.parse(savedPlayers).length === 0) {
      setPlayers(['Người chơi 1', 'Người chơi 2', 'Người chơi 3', 'Người chơi 4']);
    }
    
    if (!savedChallenges || JSON.parse(savedChallenges).length === 0) {
      const initialChallenges = [
        'Hát một bài karaoke',
        'Nhảy một điệu ngẫu nhiên trong 30 giây',
        'Kể một câu chuyện hài hước',
        'Làm 10 cái hít đất',
        'Uống một ly nước trong 5 giây'
      ];
      setChallenges(initialChallenges);
    }
  }, []);

  // Save to local storage whenever data changes
  useEffect(() => {
    localStorage.setItem('mysteryBoxPlayers', JSON.stringify(players));
    localStorage.setItem('mysteryBoxChallenges', JSON.stringify(challenges));
    localStorage.setItem('mysteryBoxColor', boxColor);
    localStorage.setItem('mysteryBoxChallengeLevel', challengeLevel);
  }, [players, challenges, boxColor, challengeLevel]);

  // Update box items when selection type or data changes
  useEffect(() => {
    const items = selectionType === 'player' ? players : challenges;
    setBoxItems(items.map((item, index) => ({
      id: index,
      text: item,
      x: Math.random() * 100 - 50,
      y: Math.random() * 100 - 50,
      rotation: Math.random() * 360
    })));
  }, [selectionType, players, challenges]);

  const addPlayer = () => {
    if (newPlayer.trim() !== '') {
      setPlayers([...players, newPlayer]);
      setNewPlayer('');
      setShowPlayerInput(false);
      playSoundIfEnabled('success');
    }
  };

  const addChallenge = () => {
    if (newChallenge.trim() !== '') {
      setChallenges([...challenges, newChallenge]);
      setNewChallenge('');
      setShowChallengeInput(false);
      playSoundIfEnabled('success');
    }
  };

  const removePlayer = (index) => {
    const updatedPlayers = [...players];
    updatedPlayers.splice(index, 1);
    setPlayers(updatedPlayers);
    playSoundIfEnabled('click');
  };

  const removeChallenge = (index) => {
    const updatedChallenges = [...challenges];
    updatedChallenges.splice(index, 1);
    setChallenges(updatedChallenges);
    playSoundIfEnabled('click');
  };

  const startEditPlayer = (index) => {
    setEditIndex(index);
    setEditText(players[index]);
    setIsEditingPlayer(true);
    playSoundIfEnabled('click');
  };

  const startEditChallenge = (index) => {
    setEditIndex(index);
    setEditText(challenges[index]);
    setIsEditingChallenge(true);
    playSoundIfEnabled('click');
  };

  const saveEdit = () => {
    if (isEditingPlayer) {
      const updatedPlayers = [...players];
      updatedPlayers[editIndex] = editText;
      setPlayers(updatedPlayers);
      setIsEditingPlayer(false);
    } else if (isEditingChallenge) {
      const updatedChallenges = [...challenges];
      updatedChallenges[editIndex] = editText;
      setChallenges(updatedChallenges);
      setIsEditingChallenge(false);
    }
    
    setEditIndex(null);
    setEditText('');
    playSoundIfEnabled('success');
  };

  const cancelEdit = () => {
    setIsEditingPlayer(false);
    setIsEditingChallenge(false);
    setEditIndex(null);
    setEditText('');
    playSoundIfEnabled('click');
  };

  const shakeBox = () => {
    if (isShaking) return;
    
    const items = selectionType === 'player' ? players : challenges;
    if (items.length < 1) {
      alert(selectionType === 'player' ? 'Cần ít nhất 1 người chơi để chọn!' : 'Cần ít nhất 1 thử thách để chọn!');
      return;
    }

    setIsShaking(true);
    playSoundIfEnabled('diceRoll');

    // Animate items floating around
    const animationInterval = setInterval(() => {
      setAnimatingItems(items.map((item, index) => ({
        id: index,
        text: item,
        x: (Math.random() - 0.5) * 200,
        y: (Math.random() - 0.5) * 200,
        rotation: Math.random() * 720 - 360,
        scale: 0.8 + Math.random() * 0.4
      })));
    }, 100);

    // Stop after 3 seconds and select winner
    setTimeout(() => {
      clearInterval(animationInterval);
      
      // Select random item
      const randomIndex = Math.floor(Math.random() * items.length);
      const selectedItem = items[randomIndex];
      
      setIsShaking(false);
      
      if (selectionType === 'player') {
        setSelectedPlayer(selectedItem);
        playSoundIfEnabled('success');
      } else {
        setSelectedChallenge(selectedItem);
        playSoundIfEnabled('success');
      }
      
      // Reset animations
      setAnimatingItems([]);
    }, 3000);
  };

  const resetSelection = () => {
    setSelectedPlayer(null);
    setSelectedChallenge(null);
    setAnimatingItems([]);
    playSoundIfEnabled('click');
  };

  const getBoxColors = () => {
    switch (boxColor) {
      case 'blue':
        return ['bg-blue-500', 'bg-indigo-500', 'bg-blue-600', 'bg-indigo-600'];
      case 'green':
        return ['bg-green-500', 'bg-emerald-500', 'bg-green-600', 'bg-emerald-600'];
      case 'rainbow':
        return ['bg-red-500', 'bg-yellow-500', 'bg-green-500', 'bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-pink-500'];
      default: // pink
        return ['bg-pink-500', 'bg-purple-500', 'bg-pink-600', 'bg-purple-600'];
    }
  };

  const getItemColor = (index) => {
    const colors = getBoxColors();
    return colors[index % colors.length];
  };

  const toggleSelectionType = () => {
    setSelectionType(selectionType === 'player' ? 'challenge' : 'player');
    playSoundIfEnabled('click');
  };

  const mildChallenges = [
    'Hát một bài karaoke trong 30 giây',
    'Nhảy một điệu ngẫu nhiên trong 30 giây',
    'Kể một câu chuyện hài hước',
    'Làm 10 cái hít đất',
    'Uống một ly nước trong 5 giây',
    'Bắt chước một loài động vật trong 20 giây',
    'Tạo một tác phẩm nghệ thuật trong 1 phút',
    'Chơi trò đoán từ với mọi người',
    'Bịt mắt và vẽ một con vật',
    'Đọc một bài thơ tự sáng tác'
  ];

  const spicyChallenges = [
    'Kể về kỷ niệm đáng xấu hổ nhất',
    'Cho người khác đặt câu hỏi và phải trả lời thật lòng',
    'Đọc tin nhắn gần đây nhất của bạn',
    'Gọi điện cho một người bạn và nói một câu kỳ lạ',
    'Nhảy sexy trong 30 giây',
    'Uống một ly đồ uống pha trộn do người khác pha',
    'Để người khác vẽ lên mặt bạn',
    'Tỏ tình với người đầu tiên bạn nhìn thấy',
    'Mặc quần áo ngược trong 10 phút',
    'Bắt chước một người trong phòng đến khi họ đoán được'
  ];

  const hotChallenges = [
    'Hôn người đối diện trong 10 giây',
    'Cởi một món đồ',
    'Kể về fantasy tình dục của bạn',
    'Massage cho người được chỉ định trong 1 phút',
    'Nhảy lap dance cho một người trong phòng',
    'Thực hiện một pose yoga sexy',
    'Để người khác cho ăn trong 1 phút',
    'Miêu tả crush của bạn không nói tên',
    'Ngồi trên đùi người bên phải trong 2 phút',
    'Cho phép một người hôn bạn ở bất kỳ đâu họ muốn'
  ];

  const addPresetChallenges = (level) => {
    let presetChallenges = [];
    switch (level) {
      case 'mild':
        presetChallenges = mildChallenges;
        break;
      case 'spicy':
        presetChallenges = spicyChallenges;
        break;
      case 'hot':
        presetChallenges = hotChallenges;
        break;
      default:
        presetChallenges = mildChallenges;
    }

    // Add challenges that don't already exist
    const newChallenges = [...challenges];
    presetChallenges.forEach(challenge => {
      if (!newChallenges.includes(challenge)) {
        newChallenges.push(challenge);
      }
    });

    setChallenges(newChallenges);
    setShowPresetChallenges(false);
    playSoundIfEnabled('success');
  };

  if (showPrivacy) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setShowPrivacy(false)}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Điều Khoản & Quyền Riêng Tư
            </h1>
            <div className="space-y-6 text-white">
              <div>
                <h2 className="text-xl font-bold mb-2">1. Giới Hạn Độ Tuổi:</h2>
                <p className="text-pink-200">
                  Ứng dụng này chỉ dành cho người từ 18 tuổi trở lên. Bằng việc sử dụng ứng dụng này, bạn xác nhận mình đã đủ tuổi theo quy định.
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">2. Quyền Sở Hữu:</h2>
                <p className="text-pink-200">
                  Game này được tạo và sở hữu độc quyền bởi Lux Homestay. Mọi hình thức sao chép, phân phối lại mà không có sự cho phép đều bị nghiêm cấm.
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">3. Nội Dung:</h2>
                <p className="text-pink-200">
                  Trò chơi có thể chứa nội dung người lớn và chỉ nên chơi trong không gian riêng tư. Người dùng tự chịu trách nhiệm về việc sử dụng trò chơi này.
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">4. Quyền Riêng Tư:</h2>
                <p className="text-pink-200">
                  Chúng tôi không thu thập, lưu trữ hay chia sẻ bất kỳ thông tin cá nhân nào của người dùng. Trò chơi được chơi hoàn toàn ngoại tuyến.
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">5. Miễn Trừ Trách Nhiệm:</h2>
                <p className="text-pink-200">
                  Lux Homestay không chịu trách nhiệm về bất kỳ hậu quả nào phát sinh từ việc sử dụng trò chơi này. Người dùng tự chịu trách nhiệm về quyết định và hành động của mình.
                </p>
              </div>
              <div className="pt-4 border-t border-white/20">
                <p className="text-center text-pink-200">
                  © 2023 Lux Homestay. Bảo lưu mọi quyền.
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowPrivacy(false)}
                className="w-full mt-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300"
              >
                Tôi Đã Hiểu & Đồng Ý
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (showSettings) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setShowSettings(false)}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Cài Đặt Hộp Bí Ẩn
            </h1>
            <div className="space-y-6">
              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Màu sắc hộp:
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      setBoxColor('pink');
                      playSoundIfEnabled('click');
                    }}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      boxColor === 'pink'
                        ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    Hồng
                  </button>
                  <button
                    onClick={() => {
                      setBoxColor('blue');
                      playSoundIfEnabled('click');
                    }}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      boxColor === 'blue'
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    Xanh
                  </button>
                  <button
                    onClick={() => {
                      setBoxColor('green');
                      playSoundIfEnabled('click');
                    }}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      boxColor === 'green'
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    Xanh lá
                  </button>
                  <button
                    onClick={() => {
                      setBoxColor('rainbow');
                      playSoundIfEnabled('click');
                    }}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      boxColor === 'rainbow'
                        ? 'bg-gradient-to-r from-red-500 via-yellow-500 to-blue-500 text-white'
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    Cầu vồng
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Mức độ thử thách:
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => {
                      setChallengeLevel('mild');
                      playSoundIfEnabled('click');
                    }}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      challengeLevel === 'mild'
                        ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white'
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    Nhẹ Nhàng 😊
                  </button>
                  <button
                    onClick={() => {
                      setChallengeLevel('spicy');
                      playSoundIfEnabled('click');
                    }}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      challengeLevel === 'spicy'
                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    Cay Nồng 😏
                  </button>
                  <button
                    onClick={() => {
                      setChallengeLevel('hot');
                      playSoundIfEnabled('click');
                    }}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      challengeLevel === 'hot'
                        ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white'
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    Nóng Bỏng 🔥
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Thử thách có sẵn:
                </label>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowPresetChallenges(true);
                    playSoundIfEnabled('click');
                  }}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-bold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiPlus} className="text-lg" />
                  Thêm thử thách có sẵn
                </motion.button>
              </div>

              <div className="pt-4 border-t border-white/20">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setPlayers([]);
                    setChallenges([]);
                    playSoundIfEnabled('notification');
                  }}
                  className="w-full mb-4 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiRefreshCw} className="text-lg" />
                  Xóa tất cả dữ liệu
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowSettings(false);
                    playSoundIfEnabled('success');
                  }}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiSave} className="text-lg" />
                  Lưu cài đặt
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (showPresetChallenges) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setShowPresetChallenges(false)}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        <div className="flex-1 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto bg-white/10 backdrop-blur-lg p-6 rounded-3xl"
          >
            <h1 className="text-2xl font-bold text-white text-center mb-6">
              Thêm thử thách có sẵn
            </h1>
            <div className="space-y-4">
              <div className="bg-white/10 p-4 rounded-xl">
                <h3 className="text-lg font-bold text-white mb-3">Nhẹ Nhàng 😊</h3>
                <ul className="list-disc list-inside text-pink-200 space-y-1 text-sm">
                  {mildChallenges.slice(0, 5).map((challenge, index) => (
                    <li key={`mild-${index}`}>
                      {challenge}
                    </li>
                  ))}
                  <li className="text-pink-300">...và {mildChallenges.length - 5} thử thách khác</li>
                </ul>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => addPresetChallenges('mild')}
                  className="w-full mt-3 bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-2 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiPlus} className="text-sm" />
                  Thêm thử thách nhẹ nhàng
                </motion.button>
              </div>

              <div className="bg-white/10 p-4 rounded-xl">
                <h3 className="text-lg font-bold text-white mb-3">Cay Nồng 😏</h3>
                <ul className="list-disc list-inside text-pink-200 space-y-1 text-sm">
                  {spicyChallenges.slice(0, 5).map((challenge, index) => (
                    <li key={`spicy-${index}`}>
                      {challenge}
                    </li>
                  ))}
                  <li className="text-pink-300">...và {spicyChallenges.length - 5} thử thách khác</li>
                </ul>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => addPresetChallenges('spicy')}
                  className="w-full mt-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold py-2 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiPlus} className="text-sm" />
                  Thêm thử thách cay nồng
                </motion.button>
              </div>

              <div className="bg-white/10 p-4 rounded-xl">
                <h3 className="text-lg font-bold text-white mb-3">Nóng Bỏng 🔥</h3>
                <ul className="list-disc list-inside text-pink-200 space-y-1 text-sm">
                  {hotChallenges.slice(0, 5).map((challenge, index) => (
                    <li key={`hot-${index}`}>
                      {challenge}
                    </li>
                  ))}
                  <li className="text-pink-300">...và {hotChallenges.length - 5} thử thách khác</li>
                </ul>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => addPresetChallenges('hot')}
                  className="w-full mt-3 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-bold py-2 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiPlus} className="text-sm" />
                  Thêm thử thách nóng bỏng
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // List Panel Modal for Mobile
  const ListPanel = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-end"
      onClick={() => setShowListPanel(false)}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        className="w-full max-h-[80vh] bg-white/10 backdrop-blur-lg rounded-t-3xl p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">
            {selectionType === 'player' ? '👥 Người Chơi' : '🎯 Thử Thách'} ({(selectionType === 'player' ? players : challenges).length})
          </h2>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => {
              if (selectionType === 'player') {
                setShowPlayerInput(true);
              } else {
                setShowChallengeInput(true);
              }
              playSoundIfEnabled('click');
            }}
            className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full"
          >
            <SafeIcon icon={FiPlus} className="text-lg" />
          </motion.button>
        </div>

        {(selectionType === 'player' ? showPlayerInput : showChallengeInput) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4"
          >
            <div className="flex gap-2">
              <input
                type="text"
                value={selectionType === 'player' ? newPlayer : newChallenge}
                onChange={(e) => {
                  if (selectionType === 'player') {
                    setNewPlayer(e.target.value);
                  } else {
                    setNewChallenge(e.target.value);
                  }
                }}
                className="flex-1 bg-white/20 text-white border border-white/30 rounded-xl px-4 py-2 focus:outline-none focus:border-white/50"
                placeholder={selectionType === 'player' ? 'Tên người chơi...' : 'Nhập thử thách...'}
                autoFocus
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={selectionType === 'player' ? addPlayer : addChallenge}
                className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-xl"
              >
                <SafeIcon icon={FiCheck} className="text-lg" />
              </motion.button>
            </div>
          </motion.div>
        )}

        <div className="space-y-2">
          {(selectionType === 'player' ? players : challenges).length === 0 ? (
            <p className="text-pink-200 text-center py-8">
              {selectionType === 'player' 
                ? 'Chưa có người chơi nào. Hãy thêm người chơi để bắt đầu!' 
                : 'Chưa có thử thách nào. Hãy thêm thử thách để bắt đầu!'
              }
            </p>
          ) : (
            (selectionType === 'player' ? players : challenges).map((item, index) => (
              <div
                key={`${selectionType}-${index}`}
                className={`flex items-center justify-between bg-white/10 hover:bg-white/20 rounded-xl p-3 transition-all ${
                  (selectionType === 'player' ? selectedPlayer : selectedChallenge) === item ? 'border-l-4 border-pink-500' : ''
                }`}
              >
                {((selectionType === 'player' && isEditingPlayer) || (selectionType === 'challenge' && isEditingChallenge)) && editIndex === index ? (
                  <div className="flex-1 flex gap-2">
                    <input
                      type="text"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="flex-1 bg-white/20 text-white border border-white/30 rounded-lg px-3 py-1 focus:outline-none focus:border-white/50"
                      autoFocus
                    />
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={saveEdit}
                      className="bg-green-500 hover:bg-green-600 text-white p-1 rounded-lg"
                    >
                      <SafeIcon icon={FiCheck} className="text-sm" />
                    </motion.button>
                  </div>
                ) : (
                  <>
                    <span className="text-white flex-1 mr-2 break-words">{item}</span>
                    <div className="flex gap-2 shrink-0">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => {
                          if (selectionType === 'player') {
                            startEditPlayer(index);
                          } else {
                            startEditChallenge(index);
                          }
                        }}
                        className="text-white/70 hover:text-white"
                      >
                        <SafeIcon icon={FiEdit} className="text-sm" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => {
                          if (selectionType === 'player') {
                            removePlayer(index);
                          } else {
                            removeChallenge(index);
                          }
                        }}
                        className="text-white/70 hover:text-red-400"
                      >
                        <SafeIcon icon={FiTrash2} className="text-sm" />
                      </motion.button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowListPanel(false)}
          className="w-full mt-6 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-3 px-4 rounded-xl shadow-lg"
        >
          Đóng
        </motion.button>
      </motion.div>
    </motion.div>
  );

  return (
    <div className="min-h-screen p-4 flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')}
          className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        <div className="flex gap-2">
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => {
              setShowSettings(true);
              playSoundIfEnabled('click');
            }}
            className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
          >
            <SafeIcon icon={FiSettings} className="text-xl" />
          </motion.button>
          <SoundToggle className="bg-white/20" />
        </div>
      </div>

      <motion.h1
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-bold text-white text-center mb-6"
      >
        📦 Hộp Bí Ẩn 📦
      </motion.h1>

      {/* Main Mystery Box - Mobile Optimized */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="relative mb-6">
          {/* Mystery Box */}
          <motion.div
            ref={boxRef}
            animate={isShaking ? {
              x: [0, -10, 10, -10, 10, 0],
              y: [0, -5, 5, -5, 5, 0],
              rotate: [0, -2, 2, -2, 2, 0]
            } : {}}
            transition={{ duration: 0.2, repeat: isShaking ? Infinity : 0 }}
            className="relative w-72 h-72 bg-gradient-to-br from-purple-600 to-pink-600 rounded-3xl shadow-2xl overflow-hidden"
          >
            {/* Box lid */}
            <motion.div
              animate={isShaking ? { y: [-5, -15, -5] } : { y: 0 }}
              transition={{ duration: 0.3, repeat: isShaking ? Infinity : 0 }}
              className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-t-3xl border-b-4 border-white/30"
            >
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <SafeIcon icon={FiGift} className="text-3xl text-white" />
              </div>
            </motion.div>

            {/* Box body */}
            <div className="absolute top-16 left-0 right-0 bottom-0 bg-gradient-to-br from-purple-600 to-pink-600 rounded-b-3xl">
              {/* Floating items inside box */}
              <div className="absolute inset-0 overflow-hidden rounded-b-3xl">
                <AnimatePresence>
                  {(isShaking ? animatingItems : boxItems.slice(0, 6)).map((item, index) => (
                    <motion.div
                      key={`item-${item.id}`}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{
                        opacity: 0.8,
                        scale: item.scale || 1,
                        x: item.x,
                        y: item.y,
                        rotate: item.rotation
                      }}
                      exit={{ opacity: 0, scale: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`absolute top-1/2 left-1/2 w-8 h-8 ${getItemColor(index)} rounded-lg flex items-center justify-center text-white text-xs font-bold shadow-lg`}
                      style={{ transform: 'translate(-50%, -50%)' }}
                    >
                      {selectionType === 'player' ? '👤' : '🎯'}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {/* Box question mark when not shaking */}
            {!isShaking && (
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-6xl">
                ❓
              </div>
            )}
          </motion.div>
        </div>

        {/* Control Buttons */}
        <div className="w-full max-w-sm space-y-4">
          {/* Shake button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={shakeBox}
            disabled={isShaking || (selectionType === 'player' ? players.length < 1 : challenges.length < 1)}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-2xl shadow-lg flex items-center justify-center gap-2 text-lg"
          >
            {isShaking ? (
              <div className="animate-spin rounded-full h-6 w-6 border-4 border-white border-t-transparent"></div>
            ) : (
              <>
                <SafeIcon icon={FiBox} className="text-2xl" />
                Lắc Hộp {selectionType === 'player' ? 'Người Chơi' : 'Thử Thách'}
              </>
            )}
          </motion.button>

          {/* Toggle selection type */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleSelectionType}
            disabled={isShaking}
            className="w-full bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-xl shadow-lg flex items-center justify-center gap-2"
          >
            <span className="text-lg">{selectionType === 'player' ? '👥' : '🎯'}</span>
            Đổi sang {selectionType === 'player' ? 'Hộp Thử Thách' : 'Hộp Người Chơi'}
          </motion.button>

          {/* View List Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowListPanel(true)}
            className="w-full bg-white/20 hover:bg-white/30 text-white font-bold py-3 px-6 rounded-xl flex items-center justify-center gap-2"
          >
            <span className="text-lg">{selectionType === 'player' ? '👥' : '🎯'}</span>
            Xem Danh Sách ({(selectionType === 'player' ? players : challenges).length})
          </motion.button>

          {/* Result display */}
          {(selectedPlayer || selectedChallenge) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/10 backdrop-blur-lg p-6 rounded-3xl"
            >
              <h3 className="text-xl font-bold text-white text-center mb-2">
                {selectedPlayer ? '👤 Người được chọn' : '🎯 Thử thách'}
              </h3>
              <p className="text-xl text-pink-200 text-center font-medium mb-4">
                {selectedPlayer || selectedChallenge}
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={resetSelection}
                className="w-full bg-white/20 hover:bg-white/30 text-white font-bold py-2 px-4 rounded-xl flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiRefreshCw} className="text-lg" />
                Đặt lại
              </motion.button>
            </motion.div>
          )}
        </div>

        {/* Instructions - Compact for Mobile */}
        <div className="mt-6 bg-white/10 backdrop-blur-lg p-4 rounded-2xl w-full max-w-sm">
          <h3 className="text-lg font-bold text-white mb-3 text-center">Hướng Dẫn</h3>
          <ul className="list-disc list-inside text-pink-200 space-y-1 text-sm">
            <li>Thêm người chơi và thử thách</li>
            <li>Chọn loại hộp cần lắc</li>
            <li>Nhấn "Lắc Hộp" để chọn ngẫu nhiên</li>
          </ul>
          <div className="mt-3 pt-3 border-t border-white/20 text-center">
            <button
              onClick={() => setShowPrivacy(true)}
              className="text-pink-200 text-xs underline"
            >
              Điều khoản & Quyền riêng tư
            </button>
            <p className="text-pink-200 text-xs mt-1">
              © 2023 Lux Homestay
            </p>
          </div>
        </div>
      </div>

      {/* List Panel Modal */}
      <AnimatePresence>
        {showListPanel && <ListPanel />}
      </AnimatePresence>
    </div>
  );
};

export default MysteryBoxGame;